import { isDefined } from "@clipboard-health/util-ts";
import { get, post } from "@src/appV2/api/parsedApi";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics";
import { type TimeSlot } from "@src/appV2/redesign/Shift/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { differenceInMinutes, getHours, getMinutes } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- @clipboard-health/date-time doesn't export utcToZonedTime equivalent
import { utcToZonedTime } from "date-fns-tz";
import { z } from "zod";

import {
  applyOptimisticUpdate,
  type ShiftTimeData,
  type ShiftTimesCachedData,
} from "./updateDefaultShiftTimesOptimistic";
import {
  getQualificationsWithSlotOverrides,
  type QualificationSpecificOverrides,
  shiftTimeDefinitionSchema,
} from "./useGetShiftTimes";

const updateShiftTimesBodySchema = z.object({
  data: z.object({
    start: z.object({
      hour: z.number(),
      minute: z.number(),
    }),
    duration: z.object({
      hours: z.number(),
      minutes: z.number(),
    }),
  }),
  facilityId: z.string(),
  name: z.enum(["am", "pm", "noc", "ori"]),
  qualification: z.string().optional(),
});

const updateShiftTimesResponseSchema = z
  .object({
    _id: z.string(),
    facilityId: z.string(),
  })
  .passthrough();

const getShiftTimesResponseSchema = z.object({
  standardDefs: z
    .object({
      am: shiftTimeDefinitionSchema.optional(),
      pm: shiftTimeDefinitionSchema.optional(),
      noc: shiftTimeDefinitionSchema.optional(),
    })
    .optional(),
  qualificationSpecific: z
    .record(z.string(), z.record(z.string(), shiftTimeDefinitionSchema))
    .optional(),
  extraDefs: z.array(shiftTimeDefinitionSchema.extend({ name: z.string() })).optional(),
});

const emptyQueryParamsSchema = z.object({});

function computeShiftTimeData(startTime: Date, endTime: Date, timezone: string): ShiftTimeData {
  const zonedStart = utcToZonedTime(startTime, timezone);
  const zonedEnd = utcToZonedTime(endTime, timezone);
  const durationTotalMinutes = differenceInMinutes(zonedEnd, zonedStart);
  return {
    start: { hour: getHours(zonedStart), minute: getMinutes(zonedStart) },
    duration: { hours: Math.floor(durationTotalMinutes / 60), minutes: durationTotalMinutes % 60 },
  };
}

interface UpdateShiftTimeForQualificationParams {
  url: string;
  shiftTimeData: ShiftTimeData;
  workplaceId: string;
  timeSlot: TimeSlot;
  qualification: string;
}

async function updateShiftTimeForQualification(params: UpdateShiftTimeForQualificationParams) {
  const { url, shiftTimeData, workplaceId, timeSlot, qualification } = params;
  return await post({
    url,
    data: {
      data: shiftTimeData,
      facilityId: workplaceId,
      name: timeSlot,
      qualification,
    },
    bodySchema: updateShiftTimesBodySchema,
    responseSchema: updateShiftTimesResponseSchema,
  });
}

/**
 * AM/PM/NOC update `standardDefs[name]`; "ori" updates `extraDefs[name="ori"]`.
 * Per-qualification overrides only exist for the AM/PM/NOC standard slots, so
 * the "ori" variant rejects `qualification` / `currentQualification` at the
 * type level — callers cannot compile their way into sending those fields.
 */
type UpdateDefaultShiftTimesParams =
  | {
      workplaceId: string;
      timeSlot: "ori";
      startTime: Date;
      endTime: Date;
      qualification?: never;
      currentQualification?: never;
      timezone: string;
    }
  | {
      workplaceId: string;
      timeSlot: TimeSlot;
      startTime: Date;
      endTime: Date;
      /** If undefined, updates standardDefs (apply to all). */
      qualification?: string;
      /** When applying to all, also updates this qualification's override. */
      currentQualification?: string;
      timezone: string;
    };

export function useUpdateDefaultShiftTimes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateDefaultShiftTimesParams) => {
      const {
        workplaceId,
        timeSlot,
        startTime,
        endTime,
        qualification,
        currentQualification,
        timezone,
      } = params;
      const updateUrl = `${environmentConfig.REACT_APP_BASE_API_URL}/shiftTimes/update`;
      const shiftTimeData = computeShiftTimeData(startTime, endTime, timezone);
      const isOrientation = timeSlot === "ori";

      // For "Apply to all", find qualifications with overrides to update them too
      let qualificationsToUpdate: string[] = [];
      if (!isDefined(qualification) && !isOrientation) {
        const qualificationsSet = new Set<string>();
        if (isDefined(currentQualification)) {
          qualificationsSet.add(currentQualification);
        }
        const getUrl = `${environmentConfig.REACT_APP_BASE_API_URL}/shiftTimes/get/${workplaceId}`;
        const response = await get({
          url: getUrl,
          queryParams: {},
          queryParamsSchema: emptyQueryParamsSchema,
          responseSchema: getShiftTimesResponseSchema,
        });
        for (const q of getQualificationsWithSlotOverrides(
          response.data.qualificationSpecific as QualificationSpecificOverrides | undefined,
          timeSlot
        )) {
          qualificationsSet.add(q);
        }
        for (const query of queryClient.getQueryCache().getAll()) {
          const [queryType, qWorkplaceId, qQualification] = query.queryKey;
          if (
            queryType === "shiftTimes" &&
            qWorkplaceId === workplaceId &&
            typeof qQualification === "string"
          ) {
            qualificationsSet.add(qQualification);
          }
        }
        // eslint-disable-next-line unicorn/prefer-spread -- Array.from needed for proper type inference
        qualificationsToUpdate = Array.from(qualificationsSet);
      }

      const mainResponse = await post({
        url: updateUrl,
        data: {
          data: shiftTimeData,
          facilityId: workplaceId,
          name: timeSlot,
          ...(!isOrientation && isDefined(qualification) ? { qualification } : {}),
        },
        bodySchema: updateShiftTimesBodySchema,
        responseSchema: updateShiftTimesResponseSchema,
      });

      if (!isDefined(qualification) && !isOrientation && qualificationsToUpdate.length > 0) {
        await Promise.allSettled(
          // eslint-disable-next-line @typescript-eslint/promise-function-async -- returns promise from helper
          qualificationsToUpdate.map((qual) =>
            updateShiftTimeForQualification({
              url: updateUrl,
              shiftTimeData,
              workplaceId,
              timeSlot,
              qualification: qual,
            })
          )
        );
      }

      return mainResponse.data;
    },
    onMutate: async (variables) => {
      const { workplaceId, timeSlot, startTime, endTime, qualification, timezone } = variables;
      const shiftTimeData = computeShiftTimeData(startTime, endTime, timezone);

      const queryPredicate = (query: { queryKey: readonly unknown[] }): boolean =>
        query.queryKey[0] === "shiftTimes" && query.queryKey[1] === workplaceId;

      await queryClient.cancelQueries({ predicate: queryPredicate });

      const previousData = new Map<readonly unknown[], ShiftTimesCachedData | undefined>();
      queryClient
        .getQueriesData<ShiftTimesCachedData>({ predicate: queryPredicate })
        .forEach(([queryKey, data]) => {
          previousData.set(queryKey, data);
        });

      queryClient.setQueriesData<ShiftTimesCachedData>(
        { predicate: queryPredicate },
        (existingData) =>
          applyOptimisticUpdate(existingData, timeSlot, shiftTimeData, qualification)
      );

      return { previousData };
    },
    onError: (_error, _variables, context) => {
      context?.previousData.forEach((data, queryKey) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "shiftTimes" && query.queryKey[1] === variables.workplaceId,
      });
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_DEFAULT_SHIFT_TIMES_FAILURE,
    },
  });
}
